import { Component, ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Qué se muestra en lugar de la sección que falló. */
  fallback?: ReactNode;
  /** Nombre de la sección, para identificarla en el log. */
  name?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Aísla el fallo de una sección para que no tumbe la página entera.
 * Sin esto, un campo ausente en una respuesta del API deja la app en blanco.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { name } = this.props;
    const label = name ? `[ErrorBoundary: ${name}]` : '[ErrorBoundary]';

    // eslint-disable-next-line no-console
    console.error(label, error, errorInfo.componentStack);
  }

  render() {
    const { hasError } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      return (
        fallback ?? (
          <div data-testid="error-boundary-fallback">
            No fue posible cargar esta sección.
          </div>
        )
      );
    }

    return children;
  }
}

export default ErrorBoundary;
